import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  useUserTokens,
  useNftBalances,
  useTokenBalances,
  useDevMode,
  useSelectedChain,
  useLocales,
} from '../state'
import DabClient from '../clients/DabClient'
import useConfig from '../hooks/useConfig'
import useWallet from '../hooks/useWallet'
import { logger } from '../logger'
import { getTokenBalance, getTokenBalances } from '../utils/getTokenBalances'
import OrdinalsClient from '../clients/OrdinalsClient'
import { type NFTCollection, type PlugToken, Chain } from '../interfaces/common'
import { ICP_ICP_ID, ODINBTC_ID } from '../constants/tokenIds'
import { groupBy } from '../utils/miscHelper'

export interface TokenBalance {
  token: PlugToken
  balance: number
  usdBalance?: number
  tokenPrice?: number
  priceChange?: number
  fee?: number
}

interface TokenBalanceContextType {
  balances: TokenBalance[]
  walletNfts: NFTCollection[]
  nftsLoading: boolean
  refreshBalance: (token: PlugToken) => void
  findBalance: (query?: { id?: string; address?: string }) => TokenBalance | undefined
  loadNfts: () => Promise<void>
  filteredBalances: TokenBalance[]
  groupedBalances: GroupedBalance[]
  isEmptyWallet: boolean
}

export const TokenBalanceContext = createContext<TokenBalanceContextType>(null as any)

export interface GroupedBalance {
  id: string
  name: string
  symbol: string
  totalBalance: number
  logo: string
  balances: TokenBalance[]
  usdBalance?: number
  priceChange?: number
}

export const tokenBalanceToBalanceGroup = (tokenBalance: TokenBalance) => {
  const { token, priceChange, usdBalance, balance } = tokenBalance

  return {
    id: token.id,
    name: token.name,
    symbol: token.symbol,
    totalBalance: balance,
    logo: token.logo,
    balances: [tokenBalance],
    usdBalance,
    priceChange,
  }
}

const BalanceProvider: FC<PropsWithChildren> = ({ children }) => {
  const [nftsLoading, setNftsLoading] = useState(false)

  const { tokens: allTokens, tokenPricesWithChange, tokenGroups } = useConfig()
  const { savedTokens } = useUserTokens()

  const { wallet } = useWallet()

  const { balances: cachedBalances, setBalances, setBalance } = useTokenBalances()
  const { nftBalances, setNftBalances } = useNftBalances()
  const { activeNetwork, networkTokens, devEnabled } = useDevMode()

  const { selectedChain } = useSelectedChain()
  const { userLocale } = useLocales()

  const tokens = useMemo(() => {
    if (devEnabled)
      return (networkTokens[activeNetwork || ''] || []).map((token) => token.principalId)

    return [ICP_ICP_ID, ...(savedTokens[wallet.principalId || ''] || [])]
  }, [savedTokens, wallet, devEnabled])

  const loadAllTokensBalances = useCallback(async () => {
    const relevantTokens = allTokens.filter((t) => tokens.includes(t.id))
    const balances = await getTokenBalances(wallet, relevantTokens)

    setBalances(wallet.principalId, balances)
  }, [wallet.accountId, wallet.principalId, allTokens])

  useEffect(() => {
    loadAllTokensBalances().catch(logger.debug)
  }, [loadAllTokensBalances])

  const refreshBalance = useCallback(
    async (token: PlugToken) => {
      try {
        const balance = await getTokenBalance(wallet, token)

        setBalance(token.id, wallet.principalId, balance)
      } catch (e) {
        logger.debug('Failed to refresh balance', { originalError: e })
      }
    },
    [wallet.principalId, setBalance]
  )

  const balances: TokenBalance[] = useMemo(() => {
    const currentWalletBalances = cachedBalances[wallet.principalId] || []
    const tokenIds = new Set([...tokens, ...currentWalletBalances.map((b) => b.id)])

    return Array.from(tokenIds)
      .map((id) => {
        const token = allTokens.find((dt) => dt.id === id)
        const balance = currentWalletBalances.find((tb) => tb.id === id)

        const tokenPrice = id === ODINBTC_ID ? tokenPricesWithChange.btc : tokenPricesWithChange[id]
        const usdBalance = tokenPrice ? (balance?.balance || 0) * tokenPrice.price : undefined

        return {
          token: token!,
          balance: balance?.balance || 0,
          tokenPrice: tokenPrice?.price,
          priceChange: tokenPrice?.priceChange,
          usdBalance,
        }
      })
      .filter((tb) => !!tb.token)
      .sort((left, right) => (right.usdBalance || 0) - (left.usdBalance || 0))
  }, [allTokens, tokens, cachedBalances, wallet.principalId, tokenPricesWithChange])

  const findBalance: TokenBalanceContextType['findBalance'] = useCallback(
    (query) => {
      if (!query) return

      return balances.find((b) => {
        if (query.id) {
          return b.token.id === query.id
        } else if (query.address && b.token.address) {
          return b.token.address.toLowerCase() === query.address.toLowerCase()
        }
      })
    },
    [JSON.stringify(balances)]
  )

  const loadNfts = useCallback(async () => {
    setNftsLoading(true)

    try {
      const client = new DabClient()
      const ordinalClient = new OrdinalsClient()

      const [nfts, ordinals] = await Promise.all([
        client.getUserNfts(wallet.principalId),
        ordinalClient.getOrdinals(wallet.btcAddress || ''),
      ])

      setNftBalances(wallet.principalId, [
        ...nfts,
        ...ordinals.map((o) => ({
          canisterId: `ordinal-${o.number}`,
          name: o?.metadata?.Name || `Ordinal: ${o.number}`,
          nfts: [o],
          icon: o.url,
          chain: Chain.BTC,
        })),
      ])
    } catch (e) {
      logger.error('Error loading NFTs', { originalError: e })
    } finally {
      setNftsLoading(false)
    }
  }, [wallet.principalId])

  useEffect(() => {
    if (devEnabled) return

    loadNfts().catch(logger.error)
  }, [wallet.principalId, wallet.btcAddress, setNftBalances, devEnabled])

  const relevantTokenBalances = balances.filter(
    (b) => b.token.id === ICP_ICP_ID || savedTokens[wallet.principalId]?.includes(b.token.id)
  )

  const filteredBalances = useMemo(() => {
    return selectedChain === 'all'
      ? relevantTokenBalances
      : relevantTokenBalances.filter((b) => b.token.chain === selectedChain)
  }, [selectedChain, relevantTokenBalances, userLocale])

  const groupedBalances: GroupedBalance[] = useMemo(() => {
    const ungroupedBalances = filteredBalances.filter((t) => !t.token.groupId)
    const groupedBalances = filteredBalances.filter((t) => t.token.groupId)

    const balancesByGroupId = groupBy(groupedBalances, (balance) => balance.token.groupId!)
    const ungroupedBalanceGroups = ungroupedBalances.map(tokenBalanceToBalanceGroup)

    const groupedBalanceGroups = Object.entries(balancesByGroupId).map(([groupId, tokens]) => {
      const tokenGroup = tokenGroups?.find((group) => group.id === groupId)

      const totalBalance = tokens.reduce((sum, token) => sum + token.balance, 0)
      const totalUsdBalance = tokens.reduce((sum, token) => sum + (token.usdBalance || 0), 0)

      return {
        id: groupId,
        name: tokenGroup?.name || tokens[0].token.name,
        symbol: tokenGroup?.symbol || tokens[0].token.symbol,
        totalBalance,
        logo: tokenGroup?.logoUrl || tokens[0].token.logo,
        balances: tokens,
        usdBalance: totalUsdBalance > 0 ? totalUsdBalance : undefined,
        priceChange: tokens[0].priceChange,
      }
    })

    return [...ungroupedBalanceGroups, ...groupedBalanceGroups].sort(
      (a, b) => (b.usdBalance || 0) - (a.usdBalance || 0)
    )
  }, [filteredBalances, tokenGroups])

  const isEmptyWallet = useMemo(() => {
    return balances.reduce((acc, next) => acc + next.balance, 0) == 0
  }, [balances])

  const value = useMemo(() => {
    return {
      balances,
      walletNfts: nftBalances[wallet.principalId] || [],
      nftsLoading,
      refreshBalance,
      findBalance,
      loadNfts,
      filteredBalances,
      groupedBalances,
      isEmptyWallet,
    }
  }, [
    balances,
    nftsLoading,
    nftBalances,
    refreshBalance,
    findBalance,
    loadNfts,
    filteredBalances,
    groupedBalances,
    isEmptyWallet,
  ])

  return <TokenBalanceContext.Provider value={value}>{children}</TokenBalanceContext.Provider>
}

export default BalanceProvider
